import { faroCoreApiClient } from "@api/faro-core-api-client";
import { CompanyId } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { assertValue } from "@utils/assert-utils";

// ------   Mocks   ---------------------------------------------------------------------------------------------------------
// These mocked API responses are used when accessing a project of a foreign workspace with ?enableDev=true and HbAdmin role.

interface DevModeMocks {
  /** Mock response for: GET /v3/company/:companyId/context */
  companyContext: FaroCoreApiTypes.GetCompanyContext;

  /** Mock response for: GET /v3/dashboard/company/:companyId/member?types=user%2Cuser-group%2Cuser-invite */
  members: FaroCoreApiTypes.CompanyMemberBase[];

  /** Mock response for: GET /v3/company/:companyId/user-groups */
  teams: FaroCoreApiTypes.Team[];
}

/**
 * Mocked API responses when accessing a project of a foreign workspace with ?enableDev=true and HbAdmin role.
 * Currently it seems not required to include any special entries, empty arrays are enough.
 */
export const devModeMocks: DevModeMocks = {
  companyContext: {
    features: [],
    featuresAvailable: [],
    hasCoreProduct: true,
    userRoles: [],
    quotas: [],
    storage: {
      projects: {
        total: {
          sizeInBytes: 762363464,
          count: 2,
        },
      },
    },
    // trial: Not needed for our purposes.
  },

  members: [],
  teams: [],
};

// -----------------------------------------------------------------------------------------------

/**
 *  @returns The context of the given company, or fallback values if (error && devMode),
 *           for usage with HbAdmin role.
 */
export function getCompanyContext(
  companyId: CompanyId,
  isDevModeEnabled: boolean
): Promise<FaroCoreApiTypes.GetCompanyContext> {
  return faroCoreApiClient.company
    .getCompanyContext({ companyId })
    .catch((error) => {
      if (isDevModeEnabled) {
        return devModeMocks.companyContext;
      } else {
        throw error;
      }
    });
}

/**
 * @returns The user profile for the given company, or for another company if (error && devMode),
 *          for usage with HbAdmin role.
 */
export async function getCurrentUserProfileInfos(
  companyId: CompanyId,
  isDevModeEnabled: boolean
): Promise<FaroCoreApiTypes.CompanyMemberBase> {
  try {
    const data = await faroCoreApiClient.users.getCurrentUserProfileInfos(
      companyId
    );
    return data;
  } catch (error) {
    if (isDevModeEnabled) {
      try {
        // The user profile can be obtained from any company where the user is member of.
        const companies = await faroCoreApiClient.company.getCompanies();
        const userOtherCompany =
          await faroCoreApiClient.users.getCurrentUserProfileInfos(
            assertValue(companies[0].id)
          );
        // Take user-specific values only, and hard-code company-specific ones.
        return {
          email: userOtherCompany.email,
          firstName: userOtherCompany.firstName,
          lastName: userOtherCompany.lastName,
          id: userOtherCompany.id,
          identity: userOtherCompany.identity,
          permissions: [],
          role: "COMPANY_VIEWER",
        };
      } catch (errorFallback) {
        // Log the extra error, and then throw the original error below.
        // eslint-disable-next-line no-console -- Developer feature
        console.error("Error with fallback company", errorFallback);
      }
    }
    throw error;
  }
}
